/**
 * ErrorBoundary — globální safety net pro crashing komponent.
 *
 * Bez ní React unmountuje celý route tree a uživatel vidí prázdnou obrazovku.
 * Tady zobrazíme alespoň message + stack trace (jen dev) + tlačítko reload.
 */
import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, Loader2 } from "lucide-react";
import { maybeReloadForChunkError, isChunkLoadError } from "./chunk-reload.js";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  // Chunk-load chyba: zobraz neutrální „načítám" placeholder (NE children — ty by hned
  // znovu spadly → render smyčka; a NE crash UI — reload může přijít). Reload je gateovaný
  // build-id (viz chunk-reload). Když nepřijde do `CHUNK_FALLBACK_MS`, přepni na crash UI.
  chunkPending: boolean;
}

const CHUNK_FALLBACK_MS = 10_000;

export class ErrorBoundary extends Component<Props, State> {
  override state: State = { error: null, errorInfo: null, chunkPending: false };
  private fallbackTimer: number | null = null;

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Stale lazy chunk → neutrální placeholder (nerenderuj padající children ani crash UI).
    if (isChunkLoadError(error)) return { error: null, errorInfo: null, chunkPending: true };
    return { error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (isChunkLoadError(error)) {
      // Kickni ASYNCHRONNÍ build-id-gateovaný reload. Když je nasazený nový build, stránka
      // se reloadne a placeholder zmizí. Když ne (stejný build → reload nepomůže), po
      // timeoutu ukaž crash UI s ručním reloadem — ať uživatel neuvázne na spinneru navždy.
      maybeReloadForChunkError(error);
      if (this.fallbackTimer === null) {
        this.fallbackTimer = window.setTimeout(() => {
          this.setState({ chunkPending: false, error, errorInfo });
        }, CHUNK_FALLBACK_MS);
      }
      return;
    }
    this.setState({ error, errorInfo });
    // Log do console pro DevTools
    console.error("[ErrorBoundary] Render crash:", error, errorInfo);
  }

  override componentWillUnmount(): void {
    if (this.fallbackTimer !== null) window.clearTimeout(this.fallbackTimer);
  }

  override render(): ReactNode {
    if (this.state.chunkPending) {
      return (
        <main className="min-h-screen flex flex-col items-center justify-center gap-3 bg-background text-muted-foreground p-4">
          <Loader2 size={28} className="animate-spin" />
          <p className="text-sm">Načítám aktuální verzi aplikace…</p>
        </main>
      );
    }
    if (this.state.error) {
      const isDev = import.meta.env.DEV;
      return (
        <main className="min-h-screen flex items-center justify-center bg-background text-foreground p-4">
          <div className="max-w-2xl w-full bg-card border border-destructive/40 rounded-2xl p-6 shadow-xl">
            <h1 className="flex items-center gap-2 text-xl font-bold mb-2 text-destructive">
              <AlertTriangle size={20} />
              Něco se rozbilo
            </h1>
            <p className="text-sm text-muted-foreground mb-4">
              Aplikace narazila na neočekávanou chybu při vykreslování.
            </p>
            <pre className="text-xs bg-background/50 border border-border rounded p-3 overflow-x-auto mb-4 text-destructive">
              {this.state.error.message}
            </pre>
            {isDev && this.state.errorInfo && (
              <details className="text-xs text-muted-foreground mb-4">
                <summary className="cursor-pointer mb-2">Stack trace (dev only)</summary>
                <pre className="bg-background/50 border border-border rounded p-3 overflow-x-auto">
                  {this.state.error.stack}
                  {"\n\n"}
                  {this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
              >
                Znovu načíst
              </button>
              <button
                type="button"
                onClick={() => (window.location.href = "/")}
                className="px-4 py-2 rounded-lg border border-border text-sm hover:bg-secondary"
              >
                Domů
              </button>
            </div>
          </div>
        </main>
      );
    }
    return this.props.children;
  }
}
